import { useEffect, useRef } from "react";
import * as d3 from "d3";
import * as Cases from '../slices/casesSlice';
import { PieArcDatum } from "d3";
import { useAppSelector } from "../slices";
import house_icon from '../assets/House_Icon.svg';

type TrackerType = "Purchase" | "Sale" | "Remortgage" | "SecuredLending";

export interface Datum {
	id: string;
	message: string;
	value: string;
	weight: number;
	color: string;
}

const getText = (message: string, line: number, flip: boolean) => {
	const a = message?.split(" ");
	const w = a?.length;
	const wpl = Math.ceil(w / 3);
	const nl = Math.ceil(a?.length / wpl);
	if (flip && nl === 1) {
		if (line < 2) {
			return "";
		}
		line = 0;
	}
	if (flip && nl === 2) {
		if (line < 1) {
			return "";
		}
		if (line === 1) {
			line = 0;
		}
		if (line === 2) {
			line = 1;
		}
	}
	let fi = line * wpl;
	let text = "";
	for (let i = fi; (i < fi + wpl && i < a?.length); i++) {
		text += a[i] + " ";
	}
	return text.trim();
}

const DrawTrackerChart = (element: any, data: Datum[], stageData: Datum[], size: number, type: string, address: string, progress: number) => {
	const addrlines = address.replace('.', '')?.split(',');
	const PI = 3.14159265359;

	d3.select(element).select("svg").remove();
	const svg = d3
		.select(element)
		.append("svg")
		.attr("preserveAspectRatio", "xMidYMid meet")
		.attr("width", size)
		.attr("viewBox", `0 0 ${size} ${size}`)
		.append("g")
		.attr("transform", `translate(${size / 2}, ${size / 2})`);

	size -= 4;
	const outerSize = size;
	size *= 0.85;

	const arcGenerator = d3.arc<PieArcDatum<Datum>>().innerRadius(size / 3).outerRadius(size / 2).padAngle(4 / size);
	const pieGenerator = d3.pie<Datum>().value((d) => d.weight).sort(null)(data);

	const arcGeneratorOuter = d3.arc<PieArcDatum<Datum>>().innerRadius(outerSize / 2.3).outerRadius(outerSize / 2).padAngle(4 / outerSize);
	const pieGeneratorOuter = d3.pie<Datum>().value((d) => d.weight).sort(null)(stageData);

	svg.append("image")
		.attr("xlink:href", house_icon)
		.attr("width", 0.288 * size)
		.attr("height", 0.224 * size)
		.attr("x", "-" + (0.144 * size) + "px")
		.attr("y", "-" + (0.224 * size) + "px")

	svg.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("font-size", Math.ceil(0.032 * size) + "px")
		.attr("dy", "8%")
		.attr("text-anchor", "middle")
		.attr("alignment-baseline", "text-top")
		.text(type)
		.attr("clip-path", "url(#circle-clip)")

	if (addrlines[0]) {
		svg.append("text")
			.attr("font-family", "OpenSans-Bold")
			.attr("font-size", Math.ceil(0.022 * size) + "px")
			.attr("dy", "12%")
			.attr("text-anchor", "middle")
			.attr("alignment-baseline", "text-top")
			.text(addrlines[0].trim())
			.attr("clip-path", "url(#circle-clip)")
	}

	if (addrlines[1]) {
		svg.append("text")
			.attr("font-family", "OpenSans-Bold")
			.attr("font-size", Math.ceil(0.022 * size) + "px")
			.attr("dy", "14%")
			.attr("text-anchor", "middle")
			.attr("alignment-baseline", "text-top")
			.text(addrlines[1].trim())
			.attr("clip-path", "url(#circle-clip)")
	}

	if (addrlines[2]) {
		svg.append("text")
			.attr("font-family", "OpenSans-Bold")
			.attr("font-size", Math.ceil(0.022 * size) + "px")
			.attr("dy", "16%")
			.attr("text-anchor", "middle")
			.attr("alignment-baseline", "text-top")
			.text(addrlines[2].trim())
			.attr("clip-path", "url(#circle-clip)")
	}

	if (addrlines[3]) {
		svg.append("text")
			.attr("font-family", "OpenSans-Bold")
			.attr("font-size", Math.ceil(0.022 * size) + "px")
			.attr("dy", "18%")
			.attr("text-anchor", "middle")
			.attr("alignment-baseline", "text-top")
			.text(addrlines[3].trim())
			.attr("clip-path", "url(#circle-clip)")
	}

	if (addrlines[4]) {
		svg.append("text")
			.attr("font-family", "OpenSans-Bold")
			.attr("font-size", Math.ceil(0.022 * size) + "px")
			.attr("dy", "20%")
			.attr("text-anchor", "middle")
			.attr("alignment-baseline", "text-top")
			.text(addrlines[4].trim())
			.attr("clip-path", "url(#circle-clip)")
	}

	const arcsOuter = svg.selectAll().data(pieGeneratorOuter).enter();
	arcsOuter
		.append("path")
		.attr("d", (d) => {
			return arcGeneratorOuter(d)
		})
		.style("fill", (d) => {
			return d.data.color
		})
		.attr("stroke", "none")
		.attr("stroke-width", 0)
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			return function (t) {
				d.endAngle = i(t);
				return arcGeneratorOuter(d) as string;
			}
		});

	arcsOuter
		.append("path")
		.attr("id", function (d) { return d.data.id + "0" })
		.attr("d", (d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				const path = d3.path();
				path.arc(0, 0, (outerSize / 2.15) - 32, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
				return path.toString();
			}
			const path = d3.path();
			path.arc(0, 0, (outerSize / 2.15), d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
			return path.toString();
		})
		.attr("stroke", "none")
		.attr("fill", "none")
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			return function (t) {
				d.endAngle = i(t);
				if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
					const path = d3.path();
					path.arc(0, 0, (outerSize / 2.15) + 8, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
					return path.toString();
				}
				const path = d3.path();
				path.arc(0, 0, (outerSize / 2.15), d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
				return path.toString();
			}
		});

	arcsOuter
		.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("fill", "white")
		.attr("font-size", Math.ceil(0.028 * size) + "px")
		.append("textPath")
		.style("text-anchor", "middle")
		.attr("startOffset", "50%")
		.attr("xlink:href", function (d) { return "#" + d.data.id + "0" })
		.text((d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				return d.data.message;
			}
			return d.data.message;
		})

	const arcs = svg.selectAll().data(pieGenerator).enter();
	arcs
		.append("path")
		.attr("d", (d) => {
			return arcGenerator(d)
		})
		.style("fill", (d) => {
			return d.data.color
		})
		.attr("stroke", "none")
		.attr("stroke-width", 0)
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			return function (t) {
				d.endAngle = i(t);
				return arcGenerator(d) as string;
			}
		});

	arcs
		.append("path")
		.attr("id", function (d) { return d.data.id + "0" })
		.attr("d", (d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				const path = d3.path();
				path.arc(0, 0, (size / 2.15) - 32, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
				return path.toString();
			}
			const path = d3.path();
			path.arc(0, 0, (size / 2.15), d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
			return path.toString();
		})
		.attr("stroke", "none")
		.attr("fill", "none")
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			return function (t) {
				d.endAngle = i(t);
				if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
					const path = d3.path();
					path.arc(0, 0, (size / 2.15) - 32, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
					return path.toString();
				}
				const path = d3.path();
				path.arc(0, 0, (size / 2.15), d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
				return path.toString();
			}
		});

	arcs
		.append("path")
		.attr("id", function (d) { return d.data.id + "1" })
		.attr("d", (d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				const path = d3.path();
				path.arc(0, 0, (size / 2.15) - 16, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
				return path.toString();
			}
			const path = d3.path();
			path.arc(0, 0, (size / 2.15) - 16, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
			return path.toString();
		})
		.attr("stroke", "none")
		.attr("fill", "none")
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			return function (t) {
				d.endAngle = i(t);
				if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
					const path = d3.path();
					path.arc(0, 0, (size / 2.15) - 16, d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
					return path.toString();
				}
				const path = d3.path();
				path.arc(0, 0, (size / 2.15) - 16, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
				return path.toString();
			}
		});

	arcs
		.append("path")
		.attr("id", function (d) { return d.data.id + "2" })
		.attr("d", (d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				const path = d3.path();
				path.arc(0, 0, (size / 2.15), d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
				return path.toString();
			}
			const path = d3.path();
			path.arc(0, 0, (size / 2.15) - 32, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
			return path.toString();
		})
		.attr("stroke", "none")
		.attr("fill", "none")
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			return function (t) {
				d.endAngle = i(t);
				if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
					const path = d3.path();
					path.arc(0, 0, (size / 2.15), d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
					return path.toString();
				}
				const path = d3.path();
				path.arc(0, 0, (size / 2.15) - 32, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
				return path.toString();
			}
		});


	arcs
		.append("path")
		.attr("id", function (d) { return d.data.id + "3" })
		.attr("d", (d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				const path = d3.path();
				path.arc(0, 0, (size / 2.95) + Math.ceil(0.020 * size), d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
				return path.toString();
			}
			const path = d3.path();
			path.arc(0, 0, (size / 2.95) + 4, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
			return path.toString();
		})
		.attr("stroke", "none")
		.attr("fill", "none")
		.transition().duration(750)
		.attrTween("d", (d) => {
			var i = d3.interpolate(d.startAngle + 0.1, d.endAngle);
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			return function (t) {
				d.endAngle = i(t);
				if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
					const path = d3.path();
					path.arc(0, 0, (size / 2.95) + Math.ceil(0.020 * size), d.endAngle - 0.025 - (PI / 2), d.startAngle + 0.025 - (PI / 2), true);
					return path.toString();
				}
				const path = d3.path();
				path.arc(0, 0, (size / 2.95) + 4, d.startAngle + 0.025 - (PI / 2), d.endAngle - 0.025 - (PI / 2));
				return path.toString();
			}
		});

	arcs
		.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("fill", "white")
		.attr("font-size", Math.ceil(0.020 * size) + "px")
		.append("textPath")
		.style("text-anchor", "middle")
		.attr("startOffset", "50%")
		.attr("xlink:href", function (d) { return "#" + d.data.id + "0" })
		.text((d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				return getText(d.data.message, 0, true);
			}
			return getText(d.data.message, 0, false);
		})

	arcs
		.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("fill", "white")
		.attr("font-size", Math.ceil(0.020 * size) + "px")
		.append("textPath")
		.style("text-anchor", "middle")
		.attr("startOffset", "50%")
		.attr("xlink:href", function (d) { return "#" + d.data.id + "1" })
		.text((d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				return getText(d.data.message, 1, true);
			}
			return getText(d.data.message, 1, false);
		})

	arcs
		.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("fill", "white")
		.attr("font-size", Math.ceil(0.020 * size) + "px")
		.append("textPath")
		.style("text-anchor", "middle")
		.attr("startOffset", "50%")
		.attr("xlink:href", function (d) { return "#" + d.data.id + "2" })
		.text((d) => {
			const midAngle = d.startAngle + ((d.endAngle - d.startAngle) / 2);
			if (midAngle > (PI / 2) && midAngle < PI + (PI / 2)) {
				return getText(d.data.message, 2, true);
			}
			return getText(d.data.message, 2, false);
		})

	arcs
		.append("text")
		.attr("font-family", "OpenSans-Bold")
		.attr("fill", "white")
		.attr("font-size", Math.ceil(0.018 * size) + "px")
		.append("textPath")
		.style("text-anchor", "middle")
		.attr("startOffset", "50%")
		.attr("xlink:href", function (d) { return "#" + d.data.id + "3" })
		.text((d) => {
			if (d.data.value === 'Y') { return ''; }
			return d.data.value;
		})
};

const filterChartData = (trackerChartData: Cases.caseListState, trackerChartOptions: any) => {
	const visibleIds = new Set();
	const visibleStages = new Set();

	// Collect IDs where isVisible is true and track their stages
	trackerChartOptions?.forEach(({ stage, items }: { stage: string, items: { id: string, isVisible: boolean }[] }) => {
		const hasVisibleItems = items.some(({ isVisible }) => isVisible);
		if (hasVisibleItems) {
			visibleStages.add(stage); // Add stage if it has visible items
		}
		items.forEach(({ id, isVisible }) => {
			if (isVisible) {
				visibleIds.add(id);
			}
		});
	});

	// Filter tracker items based on visible IDs
	const filteredTracker = trackerChartData.tracker.filter(item => visibleIds.has(item.id));

	// Filter stages based on visible stages
	const filteredStages = trackerChartOptions?.filter(({ stage }: { stage: string }) => visibleStages.has(stage));

	return {
		tracker: filteredTracker,
		stages: filteredStages
	};
}

export const chartData = (item: Cases.caseListState | null, trackerChartOptions: any): Array<Datum> => {
	let a: Array<Datum> = [];
	const filteredTrackerChartData = filterChartData(item!, trackerChartOptions);

	if (filteredTrackerChartData && filteredTrackerChartData.tracker) {
		let ci = 0;
		for (let value of Object.values(filteredTrackerChartData.tracker)) {
			if (value.value && value.isVisible) {
				a.push({ id: value.id, message: value.message, value: value.value, weight: value.weight, color: "#98C000" });
				ci++;
			}
			if (!value.value && value.isVisible) {
				a.push({ id: value.id, message: value.message, value: value.value, weight: value.weight, color: "#4BC0E1" });
			}
		}
	}
	return a;
}

export const stageData = (item: Cases.caseListState | null, trackerChartOptions: any): Array<Datum> => {
	let a: Array<Datum> = [];
	let s: Array<string> = [];
	const filteredTrackerChartData = filterChartData(item!, trackerChartOptions);

	if (filteredTrackerChartData && filteredTrackerChartData.tracker) {
		for (let value of Object.values(filteredTrackerChartData.tracker)) {
			s.indexOf(value.stage) === -1 ? s.push(value.stage) : void (0);
		}
		for (let stage of s) {
			let weight: number = 0;
			let color: string = "#98C000";
			for (let value of Object.values(filteredTrackerChartData.tracker)) {
				if (value.stage === stage && value.isVisible) {
					weight += value.weight;
					if (!value.value) {
						color = "#6A707B";
					}
				}
			}
			if (weight > 0) {
				a.push({ id: stage, message: stage, value: '', weight: weight, color: color });
			}
		}
	}
	return a;
}

const TrackerChart = (props: { data: Cases.caseState | null | any, size: number, trackerChartChangedIndex?: number, caseType?: string }) => {
	const cases = useAppSelector(state => state.cases);
	const caseType: string = props.caseType ?? cases.case.details?.type!;
	const ref = useRef(null);

	useEffect(() => {
		if (ref.current) {
			let size = props.size;
			if ((ref.current as HTMLDivElement).clientWidth < props.size) {
				size = (ref.current as HTMLDivElement).clientWidth;
			}
			if (window.innerHeight - 350 < size) {
				size = window.innerHeight - 350;
			}
			if (props.data && props.data.details) {
				DrawTrackerChart(ref.current, chartData(props.data.details, cases.trackerChartOptions?.[caseType as TrackerType]), stageData(props.data.details, cases.trackerChartOptions?.[caseType as TrackerType]), size, props.data.details.type, (props.data.property ? props.data.property.address : ""), (cases.case.details ? cases.case.details.progress : 0));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref, cases, props.trackerChartChangedIndex, caseType]);

	return (
		<div className="DonutChart" style={{ display: 'flex', justifyContent: 'center', position: 'relative', right: '15%', width: '90%' }} ref={ref}/>
	);
}

export default TrackerChart;